/**
 * Gráfico de série temporal de um instrumento (M6) — Recharts, tema dark.
 *
 * Fiel ao protótipo V8 + slide do dono (GUPM-211):
 *  - linha de leitura (eixo de cota, esquerda) + Níveis de Controle tracejados;
 *  - Cota de boca/fundo como linhas de referência (INA/PZ);
 *  - Régua do reservatório sobreposta (eixo secundário) e Pluviometria em barras;
 *  - eixo X temporal; zoom X (Brush) e zoom Y (controles de domínio) dinâmicos.
 */
import { useEffect, useState } from 'react';
import {
  Bar, Brush, CartesianGrid, ComposedChart, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { chartAriaLabel, dateLabel, initialBrushRange, type ChartModel } from '../domain/chart-model';
import { AXIS, EIXO_B_COLOR, GRID, PLUV_COLOR, REGUA_COLOR } from './chart-theme';
import ChartLegend from './ChartLegend';

interface Props {
  model: ChartModel;
  /** Cor da linha de leitura. */
  color: string;
  /** Série do instrumento selecionado ainda carregando (ex: troca de instrumento). */
  loading?: boolean;
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

export default function InstrumentChart({ model, color, loading = false }: Props) {
  const [base0, base1] = model.cotaDomain;
  // Zoom Y: domínio sobreposto sobre o inicial; reseta ao trocar de instrumento.
  const [yDomain, setYDomain] = useState<[number, number] | null>(null);
  useEffect(() => { setYDomain(null); }, [base0, base1]);
  // Zoom X (Brush): `null` = janela inicial (última semana); vira controlado ao arrastar.
  const [brush, setBrush] = useState<{ startIndex: number; endIndex: number } | null>(null);

  if (model.points.length === 0) {
    return (
      <p className="text-jf-muted text-sm py-8 text-center">
        {loading ? 'Carregando série…' : 'Sem leituras para exibir.'}
      </p>
    );
  }

  const [lo, hi] = yDomain ?? model.cotaDomain;
  const [fullLo, fullHi] = model.fullCotaDomain;
  const fullRange = fullHi - fullLo || 1;
  const zoomY = (factor: number) => {
    const mid = (lo + hi) / 2;
    // Zoom-out TRAVA no enquadramento completo (fundo→boca + todos os níveis) — nunca
    // mostra espaço vazio além das referências; zoom-in tem um piso pra não sumir.
    const range = clamp((hi - lo) * factor, fullRange / 100, fullRange);
    let nlo = mid - range / 2;
    let nhi = mid + range / 2;
    // Empurra a janela pra dentro dos limites do enquadramento completo.
    if (nlo < fullLo) { nhi += fullLo - nlo; nlo = fullLo; }
    if (nhi > fullHi) { nlo -= nhi - fullHi; nhi = fullHi; }
    setYDomain([+Math.max(nlo, fullLo).toFixed(3), +Math.min(nhi, fullHi).toFixed(3)]);
  };

  const fmtDate = (ts: number) => dateLabel(ts, model.multiYear);
  const brushRange = brush ?? initialBrushRange(model.points, 7);

  return (
    <div>
      <div className="flex items-center justify-end gap-1 mb-1">
        <span className="text-[10px] text-jf-dim mr-1">Zoom Y</span>
        <button type="button" onClick={() => zoomY(0.8)} aria-label="Aproximar eixo Y"
          className="px-2 py-0.5 rounded border border-jf-border text-jf-muted hover:text-white text-xs">+</button>
        <button type="button" onClick={() => zoomY(1.25)} aria-label="Afastar eixo Y"
          className="px-2 py-0.5 rounded border border-jf-border text-jf-muted hover:text-white text-xs">−</button>
        <button type="button" onClick={() => setYDomain(null)} aria-label="Restaurar eixo Y"
          className="px-2 py-0.5 rounded border border-jf-border text-jf-muted hover:text-white text-xs">⟲</button>
      </div>

      <div style={{ height: 300 }} className="relative" role="img" aria-label={chartAriaLabel(model)}>
        {loading && (
          <span role="status"
            className="absolute top-1 right-2 z-10 text-[10px] text-jf-muted bg-jf-surface/80 rounded px-1.5 py-0.5">
            atualizando…
          </span>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={model.points} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={fmtDate}
              stroke={AXIS}
              tick={{ fontSize: 10 }}
              minTickGap={30}
              // Respiro nas bordas pra a barra da ponta não vazar pra fora da área.
              padding={{ left: 8, right: 8 }}
            />
            <YAxis
              yAxisId="cota"
              stroke={AXIS}
              tick={{ fontSize: 11 }}
              width={52}
              domain={[lo, hi]}
              allowDataOverflow
              label={{ value: model.unit, angle: -90, position: 'insideLeft', fill: AXIS, fontSize: 10 }}
            />
            {model.hasPluviometria && (
              // Barras crescem de baixo (0), como no protótipo. Eixo proporcional
              // (topo ~ máximo real, sem inflar) — as hastes finas/transparentes não
              // escondem a linha de leitura mesmo usando a altura toda.
              <YAxis
                yAxisId="pluv"
                orientation="right"
                domain={[0, 'auto']}
                stroke={PLUV_COLOR}
                tick={{ fontSize: 10 }}
                width={44}
                label={{ value: 'mm', angle: -90, position: 'insideRight', fill: PLUV_COLOR, fontSize: 10 }}
              />
            )}
            {model.hasRegua && (
              <YAxis yAxisId="regua" orientation="right" stroke={REGUA_COLOR} tick={{ fontSize: 10 }} width={52}
                domain={['auto', 'auto']}
                label={{ value: 'Régua (m)', angle: -90, position: 'insideRight', fill: REGUA_COLOR, fontSize: 10 }} />
            )}
            <Tooltip
              labelFormatter={(ts) => fmtDate(Number(ts))}
              contentStyle={{ background: '#1a2540', border: '1px solid #28334A', borderRadius: 8 }}
              labelStyle={{ color: AXIS }}
              itemStyle={{ color: '#fff' }}
            />

            {model.hasPluviometria && (
              // maxBarSize evita a barra "gorda" (e o vazamento nas pontas) quando a
              // janela tem poucos pontos; fica com cara de haste de chuva.
              <Bar yAxisId="pluv" dataKey="pluviometria" name="Pluviometria (mm)"
                fill={PLUV_COLOR} fillOpacity={0.28} maxBarSize={10} />
            )}
            {model.hasRegua && (
              <Line yAxisId="regua" type="monotone" dataKey="regua" name="Régua do reservatório"
                stroke={REGUA_COLOR} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
            )}

            {model.referenceLines.map((l) => {
              const isThreshold = l.kind === 'threshold';
              return (
                <ReferenceLine
                  key={l.label}
                  yAxisId="cota"
                  y={l.value}
                  stroke={l.color}
                  strokeDasharray={isThreshold ? '5 4' : undefined}
                  strokeWidth={1.5}
                  // R-A07: limiares fora de vigência ficam esmaecidos (não autoritativos).
                  strokeOpacity={isThreshold && !model.thresholdsVigentes ? 0.35 : 1}
                  label={{ value: `${l.label} (${l.value})`, fill: l.color, fontSize: 10, position: 'insideTopRight' }}
                />
              );
            })}

            <Line yAxisId="cota" type="monotone" dataKey="value"
              name={model.hasEixoB ? `Eixo A (${model.unit})` : `Leitura (${model.unit})`}
              stroke={color} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />

            {/* GUPM-270 — eixo B do tiltímetro. MESMO eixo Y do A: são a mesma
                grandeza na mesma unidade, e a régua (±5/±7,5/±15) vale para os
                dois. Escalas separadas fariam duas curvas incomparáveis parecerem
                comparáveis. Traço tracejado distingue por FORMA, não só por cor. */}
            {model.hasEixoB && (
              <Line yAxisId="cota" type="monotone" dataKey="eixoB" name={`Eixo B (${model.unit})`}
                stroke={EIXO_B_COLOR} strokeWidth={2} strokeDasharray="6 3" dot={false}
                connectNulls isAnimationActive={false} />
            )}

            <Brush
              dataKey="ts"
              height={22}
              travellerWidth={8}
              stroke={AXIS}
              fill="#0d1424"
              tickFormatter={() => ''}
              startIndex={brushRange.startIndex}
              endIndex={brushRange.endIndex}
              onChange={(e) => {
                if (e && typeof e.startIndex === 'number' && typeof e.endIndex === 'number') {
                  setBrush({ startIndex: e.startIndex, endIndex: e.endIndex });
                }
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <ChartLegend model={model} color={color} />
    </div>
  );
}
